import { useEffect, useRef } from 'react';
import anime from 'animejs';
import type { CVConfig } from '../../config/cvConfig';

interface ExperienceProps {
  experience: CVConfig['experience'];
}

export function Experience({ experience }: ExperienceProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate timeline items
            anime({
              targets: '.timeline-item',
              translateX: [-100, 0],
              opacity: [0, 1],
              duration: 800,
              delay: (_el, i) => i * 300,
              easing: 'easeOutQuart'
            });

            // Animate timeline line
            anime({
              targets: '.timeline-line',
              height: ['0%', '100%'],
              duration: 2000,
              delay: 500,
              easing: 'easeOutQuart'
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="experience" 
      className="py-20 px-4 sm:px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-orbitron font-bold neon-text mb-4">
            Experience
          </h2>
          <p className="text-xl text-white/70 font-rajdhani max-w-3xl mx-auto">
            A journey through innovation, leadership, and transformative AI implementations
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 w-0.5 bg-gradient-to-b from-[var(--synthwave-primary)] via-[var(--synthwave-secondary)] to-[var(--synthwave-accent)] timeline-line" style={{ height: '0%' }}></div>

          {/* Experience Items */}
          <div className="space-y-12">
            {experience.map((exp, index) => (
              <div key={index} className="timeline-item relative pl-20 opacity-0">
                {/* Timeline Dot */}
                <div className="absolute left-6 top-6 w-4 h-4 bg-[var(--synthwave-neon)] rounded-full border-4 border-black shadow-lg">
                  <div className="absolute inset-0 bg-[var(--synthwave-neon)] rounded-full animate-ping opacity-75"></div>
                </div>

                {/* Content Card */}
                <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-8 neon-border hover:bg-black/60 transition-all duration-300 group">
                  {/* Header */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-2 group-hover:text-white transition-colors duration-300">
                        {exp.position}
                      </h3>
                      <h4 className="text-xl font-rajdhani font-semibold text-[var(--synthwave-primary)] mb-2">
                        {exp.company}
                      </h4>
                    </div>
                    <div className="text-sm font-rajdhani text-white/60 bg-white/10 px-4 py-2 rounded-full">
                      {exp.duration}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <ul className="space-y-3 font-rajdhani text-white/80">
                      {exp.description.map((desc, descIndex) => (
                        <li key={descIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-[var(--synthwave-secondary)] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                          {desc}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Achievements */}
                  {exp.achievements && exp.achievements.length > 0 && (
                    <div className="mb-6">
                      <h5 className="text-lg font-orbitron font-semibold text-[var(--synthwave-accent)] mb-3">
                        Key Achievements
                      </h5>
                      <ul className="space-y-2 font-rajdhani text-white/80">
                        {exp.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start">
                            <svg className="w-4 h-4 text-[var(--synthwave-neon)] mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Technologies */}
                  <div>
                    <h5 className="text-lg font-orbitron font-semibold text-[var(--synthwave-secondary)] mb-3">
                      Technologies Used
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-gradient-to-r from-[var(--synthwave-primary)]/20 to-[var(--synthwave-secondary)]/20 backdrop-blur-sm rounded-full text-sm font-rajdhani text-white border border-white/20 hover:border-[var(--synthwave-neon)] transition-colors duration-300"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Experience Summary */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-8 p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-primary)]">
                {experience.length}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Positions</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-secondary)]">
                5+
              </div>
              <div className="text-sm font-rajdhani text-white/70">Years Experience</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-neon)]">
                {experience.reduce((acc, exp) => acc + (exp.achievements?.length || 0), 0)}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Achievements</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}